// bracketUtils.js

// ============================================================
// Helpers
// ============================================================

function createTBD(sourceMatchId) {
  return {
    name: "TBD",
    slt_id: null,
    sourceMatchId,
  };
}

/**
 * Creates standard tournament seed placement.
 *
 * Examples:
 *
 * 4 slots:
 * 1 vs 4
 * 2 vs 3
 *
 * 8 slots:
 * 1 vs 8
 * 4 vs 5
 * 2 vs 7
 * 3 vs 6
 *
 * This continues dynamically for:
 * 16, 32, 64, 128, etc.
 */
function generateSeedOrder(totalSlots) {
  if (totalSlots <= 1) {
    return [1];
  }

  if (totalSlots === 2) {
    return [1, 2];
  }

  /*
   * Standard mirrored / serpentine tournament layout.
   *
   * 16-slot visual:
   *
   * 1  vs 16
   * 8  vs 9
   * 5  vs 12
   * 13 vs 4
   *
   * 6  vs 11
   * 3  vs 14
   * 7  vs 10
   * 15 vs 2
   *
   * This places:
   * #1 at the very top
   * #4 at the bottom of #1's quarter
   * #3 in the opposite half
   * #2 at the very bottom
   */

  const base16 = [
    1, 16,
    8, 9,
    5, 12,
    13, 4,

    6, 11,
    3, 14,
    7, 10,
    15, 2,
  ];

  // Smaller brackets use the same mirrored pattern.
  if (totalSlots <= 16) {
    return base16.filter(
      (seed) => seed <= totalSlots
    );
  }

  // Expand dynamically:
  // 16 → 32 → 64 → 128 → etc.
  let seeds = [...base16];
  let currentSize = 16;

  while (currentSize < totalSlots) {
    const nextSize = currentSize * 2;
    const complement = nextSize + 1;

    seeds = seeds.flatMap((seed) => [
      seed,
      complement - seed,
    ]);

    currentSize = nextSize;
  }

  return seeds;
}

/**
 * Creates a normalized team with the original seed/slot information.
 */
function normalizeTeam(team, fallbackIndex) {
  const originalSlot = team?.position ?? fallbackIndex;

  return {
    ...team,
    position: originalSlot,
    _originalSlot: originalSlot,
    _slotLabel: `Slot ${originalSlot + 1}`,
  };
}

/**
 * Creates a placeholder team when no real team has been supplied.
 */
function createPlaceholderTeam(index) {
  return {
    name: `Slot ${index + 1}`,
    slt_id: null,
    position: index,
    _originalSlot: index,
    _slotLabel: `Slot ${index + 1}`,
  };
}


// ============================================================
// Generate Bracket
// ============================================================

export function generateBracket(numTeams, teamList = []) {
  const teamCount = Number(numTeams);

  // ----------------------------------------------------------
  // Validation
  // ----------------------------------------------------------

  if (!Number.isInteger(teamCount) || teamCount < 2) {
    return {
      totalRounds: 0,
      totalSlots: 0,
      numByes: 0,
      numFirstRoundGames: 0,
      rounds: [],
    };
  }

  // ----------------------------------------------------------
  // Prepare teams
  // ----------------------------------------------------------

  const hasTeams =
    Array.isArray(teamList) &&
    teamList.length > 0;

  let sortedTeams;

  if (hasTeams) {
    sortedTeams = teamList
      .map((team, index) =>
        normalizeTeam(team, index)
      )
      .sort(
        (a, b) =>
          (a.position ?? 999999) -
          (b.position ?? 999999)
      );

    // Fill any missing slots with placeholders.
    while (sortedTeams.length < teamCount) {
      sortedTeams.push(
        createPlaceholderTeam(sortedTeams.length)
      );
    }

    // Never generate more teams than requested.
    sortedTeams = sortedTeams.slice(0, teamCount);
  } else {
    sortedTeams = Array.from(
      { length: teamCount },
      (_, index) =>
        createPlaceholderTeam(index)
    );
  }

  // ----------------------------------------------------------
  // Calculate bracket size
  // ----------------------------------------------------------

  const totalRounds =
    Math.ceil(Math.log2(teamCount));

  const totalSlots =
    2 ** totalRounds;

  const numByes =
    totalSlots - teamCount;

  const seedOrder =
    generateSeedOrder(totalSlots);

  // ----------------------------------------------------------
  // Put teams into standard seeded bracket slots
  // ----------------------------------------------------------

  const seededSlots = seedOrder.map(
    (seedNumber) => {
      if (seedNumber > teamCount) {
        return null;
      }

      return sortedTeams[seedNumber - 1];
    }
  );

  // Each pair represents one possible first-round game.
  const firstStagePairs = [];

  for (
    let i = 0;
    i < seededSlots.length;
    i += 2
  ) {
    firstStagePairs.push({
      teamA: seededSlots[i] || null,
      teamB: seededSlots[i + 1] || null,
    });
  }

  const rounds = [];

  // ==========================================================
  // CASE 1
  // Perfect power-of-two bracket
  //
  // 4, 8, 16, 32, 64...
  // ==========================================================

  if (numByes === 0) {
    const round1 = firstStagePairs.map(
      (pair, index) => ({
        teamA: pair.teamA,
        teamB: pair.teamB,
        isBye: false,
        isSpacer: false,
        originalIndex: index,
        matchId: `r1-${index}`,
      })
    );

    rounds.push(round1);

    // Generate every remaining round dynamically.
    for (
      let roundNumber = 2;
      roundNumber <= totalRounds;
      roundNumber++
    ) {
      const previousRound =
        rounds[roundNumber - 2];

      const nextRound = [];

      for (
        let i = 0;
        i < previousRound.length;
        i += 2
      ) {
        const matchIndex = i / 2;

        nextRound.push({
          matchId:
            `r${roundNumber}-${matchIndex}`,

          teamA: createTBD(
            previousRound[i].matchId
          ),

          teamB: createTBD(
            previousRound[i + 1].matchId
          ),

          isBye: false,
        });
      }

      rounds.push(nextRound);
    }

    return {
      totalRounds,
      totalSlots,
      numByes,
      numFirstRoundGames:
        round1.length,
      rounds,
    };
  }

  // ==========================================================
  // CASE 2
  // Bracket contains BYEs
  //
  // Example:
  //
  // 11 teams
  // 16 total slots
  // 5 byes
  // 3 first-round games
  //
  // Winners + bye teams create the Round 2 field.
  // ==========================================================

  const round1 = [];
  const round2 = [];

  let round1MatchCounter = 0;
  let spacerCounter = 0;

  /**
   * Takes one seeded pair.
   *
   * Returns:
   *
   * DIRECT
   * A team automatically advances because opponent is a bye.
   *
   * SOURCE
   * Two teams play and Round 2 receives the winner.
   */
  const resolveFirstStagePair = (pair) => {
    const teamA = pair?.teamA || null;
    const teamB = pair?.teamB || null;

    // Two real teams = actual Round 1 game.
    if (teamA && teamB) {
      const matchId =
        `r1-${round1MatchCounter}`;

      const match = {
        teamA,
        teamB,
        isBye: false,
        isSpacer: false,
        originalIndex:
          round1MatchCounter,
        matchId,
      };

      round1MatchCounter++;

      return {
        type: "source",
        match,
        slot: createTBD(matchId),
      };
    }

    // One team = automatic advancement.
    if (teamA || teamB) {
      return {
        type: "direct",
        match: null,
        slot: teamA || teamB,
      };
    }

    return {
      type: "empty",
      match: null,
      slot: null,
    };
  };

  /**
   * Two first-stage pairs feed one Round 2 match.
   *
   * This lets the algorithm automatically create:
   *
   * team vs TBD
   * TBD vs team
   * TBD vs TBD
   * team vs team
   */
  for (
    let pairIndex = 0;
    pairIndex < firstStagePairs.length;
    pairIndex += 2
  ) {
    const leftPair =
      firstStagePairs[pairIndex];

    const rightPair =
      firstStagePairs[pairIndex + 1];

    const leftResult =
      resolveFirstStagePair(leftPair);

    const rightResult =
      resolveFirstStagePair(rightPair);

    // --------------------------------------------------------
    // Round 1 visual structure
    // --------------------------------------------------------

    const realMatches = [];

    if (leftResult.match) {
      realMatches.push(leftResult.match);
    }

    if (rightResult.match) {
      realMatches.push(rightResult.match);
    }

    // No preliminary games feed this Round 2 match.
    // Keep a spacer so bracket geometry stays aligned.
    if (realMatches.length === 0) {
      round1.push({
        isSpacer: true,
        matchId:
          `r1-spacer-${spacerCounter}`,
      });

      spacerCounter++;
    } else {
      realMatches.forEach((match) => {
        round1.push(match);
      });
    }

    // --------------------------------------------------------
    // Round 2
    // --------------------------------------------------------

    const round2MatchIndex =
      round2.length;

    round2.push({
      matchId:
        `r2-${round2MatchIndex}`,

      teamA:
        leftResult.slot ||
        { name: "TBD", slt_id: null },

      teamB:
        rightResult.slot ||
        { name: "TBD", slt_id: null },

      isBye: false,
    });
  }

  rounds.push(round1);
  rounds.push(round2);

  // ==========================================================
  // Generate Round 3 → Championship
  // ==========================================================

  for (
    let roundNumber = 3;
    roundNumber <= totalRounds;
    roundNumber++
  ) {
    const previousRound =
      rounds[roundNumber - 2];

    const nextRound = [];

    for (
      let i = 0;
      i < previousRound.length;
      i += 2
    ) {
      const leftMatch =
        previousRound[i];

      const rightMatch =
        previousRound[i + 1];

      if (!leftMatch || !rightMatch) {
        continue;
      }

      const matchIndex =
        i / 2;

      nextRound.push({
        matchId:
          `r${roundNumber}-${matchIndex}`,

        teamA: createTBD(
          leftMatch.matchId
        ),

        teamB: createTBD(
          rightMatch.matchId
        ),

        isBye: false,
      });
    }

    rounds.push(nextRound);
  }

  return {
    totalRounds,
    totalSlots,
    numByes,
    numFirstRoundGames:
      round1MatchCounter,
    rounds,
  };
}


// ============================================================
// Update Bracket Winners
// ============================================================

export function getUpdatedBracketWithWinners(
  bracketData,
  games
) {
  if (
    !bracketData?.rounds ||
    !Array.isArray(bracketData.rounds)
  ) {
    return [];
  }

  const updatedRounds =
    JSON.parse(
      JSON.stringify(bracketData.rounds)
    );

  let changed = false;

  const resetTargetSlot = (
    targetMatch,
    slotKey,
    sourceMatchId
  ) => {
    const currentSlot =
      targetMatch[slotKey];

    targetMatch[slotKey] = {
      ...currentSlot,
      name: "TBD",
      slt_id: null,
      id: null,
      position: null,
      sourceMatchId,
    };

    changed = true;
  };

  // Walk through every round except the championship.
  for (
    let roundIndex = 0;
    roundIndex < updatedRounds.length - 1;
    roundIndex++
  ) {
    const currentRound =
      updatedRounds[roundIndex];

    const nextRound =
      updatedRounds[roundIndex + 1];

    for (const match of currentRound) {
      // Spacers are visual only.
      if (match?.isSpacer) {
        continue;
      }

      const targetMatch =
        nextRound.find(
          (nextMatch) =>
            nextMatch.teamA?.sourceMatchId ===
            match.matchId ||
            nextMatch.teamB?.sourceMatchId ===
            match.matchId
        );

      if (!targetMatch) {
        continue;
      }

      const targetSlotKey =
        targetMatch.teamA?.sourceMatchId ===
          match.matchId
          ? "teamA"
          : "teamB";

      const game = games.find(
        (gameItem) =>
          gameItem.match_id ===
          match.matchId
      );

      const hasValidTeams =
        match.teamA?.slt_id != null &&
        match.teamB?.slt_id != null;

      const hasValidScore =
        game &&
        game.score_home != null &&
        game.score_away != null &&
        game.score_home !==
        game.score_away;

      // No valid winner yet.
      if (
        !hasValidTeams ||
        !hasValidScore
      ) {
        const currentAdvancedTeam =
          targetMatch[targetSlotKey];

        if (
          currentAdvancedTeam?.slt_id != null ||
          currentAdvancedTeam?.name !== "TBD"
        ) {
          resetTargetSlot(
            targetMatch,
            targetSlotKey,
            match.matchId
          );
        }

        continue;
      }

      const isHomeWinner =
        game.score_home >
        game.score_away;

      let winner;

      if (
        game.home_team_id ===
        match.teamA.slt_id
      ) {
        winner = isHomeWinner
          ? match.teamA
          : match.teamB;
      } else {
        winner = isHomeWinner
          ? match.teamB
          : match.teamA;
      }

      targetMatch[targetSlotKey] = {
        ...winner,

        // Keep the source link so this slot still knows
        // which previous match feeds into it.
        sourceMatchId:
          match.matchId,

        matchId:
          match.matchId,
      };

      changed = true;
    }
  }

  return changed
    ? updatedRounds
    : bracketData.rounds;
}